import { ActiveSheetData } from 'src/components/viewers/SheetsViewerV2/hooks/useActiveSheet';
import {
  AbstractSheetLineShape,
  SheetLineStyleSet,
} from 'src/components/viewers/SheetsViewerV2/shapes/abstract/AbstractSheetLineShape';
import { SheetShapeStyleType } from 'src/components/viewers/SheetsViewerV2/shapes/abstract/AbstractSheetShape';
import { SheetShapeDeepFragment } from 'src/gql/graphql';
import Edit2D = Autodesk.Extensions.Edit2D;
import GuiViewer3D = Autodesk.Viewing.GuiViewer3D;
import Viewer3D = Autodesk.Viewing.Viewer3D;

export class PolylineShape extends AbstractSheetLineShape {
  constructor(
    viewer: Viewer3D | GuiViewer3D,
    edit2d: Edit2D,
    activeSheetData: ActiveSheetData,
    shapeModel?: SheetShapeDeepFragment,
    line?: THREE.Vector3[],
    styleSet?: SheetLineStyleSet
  ) {
    super(viewer, edit2d, activeSheetData, shapeModel);
    const convertedLine =
      shapeModel && shapeModel.sheetShapeLine
        ? shapeModel.sheetShapeLine.points.map((point) =>
            super.convertPointToRender(point)
          )
        : line;

    if (shapeModel && !shapeModel.sheetShapeLine) {
      throw new Error('shapeModel.sheetShapeLine must be defined');
    } else if (!shapeModel && !Array.isArray(line)) {
      throw new Error('One shapeModel or point must be passed');
    } else if (!convertedLine) {
      throw new Error('Cannot define point');
    }

    this.shapes = [new Autodesk.Edit2D.Polyline(convertedLine)];
    // Place label at arc-length parameter t along the polyline (default t = 0.5)
    const labelT = 0.5;
    const labelPoint = this.getPointAtParam(convertedLine, labelT);
    this.textLabelPoints = [labelPoint];
    this.styleSet = styleSet || null;

    this.setStyle(SheetShapeStyleType.default);
  }

  public hitTest(x: number, y: number) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return this.shapes.some((shape) => shape.hitTest(x, y, 0.1));
  }

  private getPointAtParam(
    points: { x: number; y: number }[],
    t: number
  ): { x: number; y: number } {
    const clampedT = Math.max(0, Math.min(1, t));
    if (points.length === 0) {
      return { x: 0, y: 0 };
    }
    if (points.length === 1) {
      return { x: points[0].x, y: points[0].y };
    }

    const distances: number[] = [];
    let totalLength = 0;
    for (let i = 0; i < points.length - 1; i++) {
      const dx = points[i + 1].x - points[i].x;
      const dy = points[i + 1].y - points[i].y;
      const segmentLength = Math.hypot(dx, dy);
      distances.push(segmentLength);
      totalLength += segmentLength;
    }

    if (totalLength === 0) {
      // All points identical
      return { x: points[0].x, y: points[0].y };
    }

    const target = clampedT * totalLength;
    let accumulated = 0;

    for (let i = 0; i < distances.length; i++) {
      const segmentLength = distances[i];
      if (accumulated + segmentLength >= target) {
        const remaining = target - accumulated;
        const ratio = segmentLength === 0 ? 0 : remaining / segmentLength;
        const start = points[i];
        const end = points[i + 1];
        return {
          x: start.x + (end.x - start.x) * ratio,
          y: start.y + (end.y - start.y) * ratio,
        };
      }
      accumulated += segmentLength;
    }

    // Fallback to last point due to floating point rounding
    const last = points[points.length - 1];
    return { x: last.x, y: last.y };
  }
}
